import { Options, RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import { Box, Link, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import parse from 'html-react-parser';
import React, { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import { FacilityFieldsFragment, CardRichTextDescriptionLinks, SectionContentRichTextContentLinks, ContentTypeRichTextContentLinks, ContentTypeRichTextSecondaryContentLinks, TextBlockSeoContentLinks, TermsAndConditionsContentLinks, InsuranceIntroLinks, TableFootnoteLinks, Video as IVideo, Maybe, Entry, ContentSectionDescriptionLinks } from '@/graphql/__generated/sdk';
import Columns from '../Columns';
import ImageAsset from '../ImageAsset';
import SingleMessage from '../SingleMessage';
import { getUrlType } from '../TAGLink/TAGLink.helpers';
import FocalPointImage from '@shared-components/components/FocalPointImage/';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import Table from '@shared-components/components/Table/';
import VideoContent from '@shared-components/components/VideoContent';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@shared-components/context';
import { BrandThemeConfig } from '@shared-components/types';
import { dataLayerConfig } from '@utils';
import { getInnerTextForAnalytics } from '@utils/getInnerTextForAnalytics';
import { logWarning } from '@utils/miscUtils';
import { normalizeDomainLink } from '@utils/normalizeDomainLink';
import { resolveEntryLink } from '@utils/resolveButtonEntryLink';
interface ContentfulRichTextNodeTarget {
  children: React.ReactNode;
  description: string;
  linkType: string;
  sys: {
    id: string;
    linkType: string;
    type: string;
  };
  file: {
    contentType: string;
    url: string;
  };
  filename: string;
  image: {
    title: string;
  };
  focalPoint: {
    x: number;
    y: number;
  };
  path: string;
}
interface HyperLinkEntry extends Entry {
  __typename: string;
  path?: string;
}
const listSx: Record<string, unknown> = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'start',
  textAlign: 'left'
};
const getTokenBounds = (text: string, cursor: number): {
  open: number;
  close: number;
} | null => {
  const open = text.indexOf('{', cursor);
  if (open === -1) {
    return null;
  }
  const close = text.indexOf('}', open + 1);
  if (close === -1) {
    return null;
  }
  return {
    open,
    close
  };
};
const parseColorToken = (token: string): {
  color: string;
  content: string;
} | null => {
  const hasNestedBrace = token.includes('{') || token.includes('}');
  const colonIndex = token.indexOf(':');
  const hasInvalidColorPrefix = token.startsWith('#') && colonIndex === -1;
  if (hasNestedBrace || hasInvalidColorPrefix) {
    return null;
  }
  return {
    color: token.startsWith('#') && colonIndex > 0 ? token.slice(0, colonIndex) : '',
    content: colonIndex >= 0 ? token.slice(colonIndex + 1) : token
  };
};
const coloredSpan = (content: string, color: string, key: string) => <span key={key} style={{
  color
}} data-sentry-component="coloredSpan" data-sentry-source-file="RichText.helpers.tsx">
		{content}
	</span>;
export const parseChildren = (children: ReactNode): ReactNode => {
  let activeColor: string | null = null;
  const parseActiveColor = (text: string, cursor: number, color: string) => {
    const close = text.indexOf('}', cursor);
    const content = close === -1 ? text.slice(cursor) : text.slice(cursor, close);
    const segments = content ? [coloredSpan(content, color, `${content}_${cursor}`)] : [];
    return {
      segments,
      nextCursor: close === -1 ? null : close + 1
    };
  };
  const getColorPrefix = (text: string, cursor: number) => {
    const open = text.indexOf('{', cursor);
    const colon = open === -1 ? -1 : text.indexOf(':', open);
    const prefix = open === -1 || colon === -1 ? '' : text.slice(open, colon + 1);
    return /^\{(?:#[^{}:]+)?:$/.test(prefix) ? {
      open,
      prefix
    } : null;
  };
  const getUnclosedColorToken = (text: string, cursor: number) => {
    const colorPrefix = getColorPrefix(text, cursor);
    return colorPrefix ? {
      textBeforeToken: text.slice(cursor, colorPrefix.open),
      color: colorPrefix.prefix.slice(1, -1),
      nextCursor: colorPrefix.open + colorPrefix.prefix.length
    } : null;
  };
  const parseAtCursor = (text: string, cursor: number, color: string | null) => {
    if (color !== null) {
      const parsedActiveColor = parseActiveColor(text, cursor, color);
      return {
        segments: parsedActiveColor.segments,
        nextCursor: parsedActiveColor.nextCursor,
        activeColor: parsedActiveColor.nextCursor === null ? color : null,
        didParseToken: true,
        returnSegments: parsedActiveColor.nextCursor === null
      };
    }
    const bounds = getTokenBounds(text, cursor);
    if (!bounds) {
      const unclosedColorToken = getUnclosedColorToken(text, cursor);
      if (!unclosedColorToken) {
        return {
          segments: [],
          nextCursor: null,
          activeColor: null,
          didParseToken: false,
          returnSegments: false
        };
      }
      return {
        segments: [unclosedColorToken.textBeforeToken],
        nextCursor: unclosedColorToken.nextCursor,
        activeColor: unclosedColorToken.color,
        didParseToken: true,
        returnSegments: false
      };
    }
    const {
      open,
      close
    } = bounds;
    const parsedToken = parseColorToken(text.slice(open + 1, close));
    if (!parsedToken) {
      return {
        segments: [],
        nextCursor: close + 1,
        activeColor: null,
        didParseToken: false,
        returnSegments: false
      };
    }
    const segments = [...(open > cursor ? [text.slice(cursor, open)] : []), coloredSpan(parsedToken.content, parsedToken.color, `${parsedToken.content}_${open}`)];
    return {
      segments,
      nextCursor: close + 1,
      activeColor: null,
      didParseToken: true,
      returnSegments: false
    };
  };
  const parseSegments = (text: string): ReactNode[] | null => {
    const segments: ReactNode[] = [];
    let didParseToken = false;
    let cursor = 0;
    while (cursor < text.length) {
      const parsed = parseAtCursor(text, cursor, activeColor);
      segments.push(...parsed.segments);
      didParseToken ||= parsed.didParseToken;
      activeColor = parsed.activeColor;
      if (parsed.returnSegments) {
        return segments;
      }
      if (parsed.nextCursor === null) {
        break;
      }
      cursor = parsed.nextCursor;
    }
    if (!segments.length && !didParseToken) {
      return null;
    }
    if (cursor < text.length) {
      segments.push(text.slice(cursor));
    }
    return segments;
  };
  const processNode = (node: ReactNode): ReactNode => {
    if (typeof node === 'string') {
      const parsedSegments = parseSegments(node);
      if (!parsedSegments) {
        return node;
      }
      return parsedSegments;
    }
    if (isValidElement(node)) {
      return cloneElement(node as ReactElement, {
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
        children: React.Children.map(node.props.children, processNode)
      });
    }
    return node;
  };
  return React.Children.map(children, processNode);
};
const handleLinkClickDataLayer = (config: BrandThemeConfig, uri?: string, linkText?: string, officeInfo?: Maybe<FacilityFieldsFragment>) => {
  dataLayerConfig({
    event: 'navigation_click_event',
    brand: config.name,
    page_destination_url: uri || '',
    link_click_location: 'richText',
    link_text: linkText,
    website_selected_facility_id: officeInfo?.code || '',
    website_selected_facility_name: officeInfo?.name || ''
  });
};
const EntryBlockRender = (props: {
  matchingBlockEntry?: Maybe<Entry>;
  target?: ContentfulRichTextNodeTarget;
  targetSysId?: string;
  isSmallScreen: boolean;
}) => {
  const {
    matchingBlockEntry,
    target,
    targetSysId,
    isSmallScreen
  } = props;
  const entry = matchingBlockEntry as any;
  if (entry?.__typename === 'Table') {
    return <Table _id="" {...entry} />;
  }
  if (entry?.__typename === 'VideoAsset') {
    return <VideoContent richTextStylePosition={'relative'} controllableVideo={isSmallScreen && entry.mobileVideo as IVideo ? entry.mobileVideo as IVideo : entry.desktopVideo as IVideo} />;
  }
  if (entry?.__typename === 'ImageAsset') {
    return <Box sx={{
      display: 'flex',
      justifyContent: 'left'
    }}>
				<ImageAsset {...entry} dataTestId={`rich_text_image_asset_${targetSysId ?? ''}`} />
			</Box>;
  }

  // @ts-ignore
  if (matchingBlockEntry?.__typename === 'SingleMessage') {
    return <SingleMessage _id="" {...matchingBlockEntry} templateWidth={'full'} containerSx={{
      marginY: '1.5rem'
    }} />;
  }

  // @ts-ignore
  if (matchingBlockEntry?.__typename === 'Columns') {
    return (
      // @ts-ignore
      <Columns {...matchingBlockEntry} disablePaddingOn={['Top', 'Bottom']} disableItemYPaddingOn={true} disabledGutters={true} containerSx={{
        marginY: '1.5rem'
      }} />
    );
  }
  return (
    // @ts-ignore until it breaks
    <FocalPointImage image={target?.image} focalPoint={target?.focalPoint} {...docProps} data-sentry-element="FocalPointImage" data-sentry-component="EntryBlockRender" data-sentry-source-file="RichText.helpers.tsx">
			<>{target?.children}</>
		</FocalPointImage>
  );
};
export const getOptions = ({
  docProps,
  links,
  isDark = false,
  theme,
  isSmallScreen
}: {
  docProps: Record<string, unknown>;
  links?: CardRichTextDescriptionLinks | SectionContentRichTextContentLinks | ContentTypeRichTextContentLinks | TextBlockSeoContentLinks | TermsAndConditionsContentLinks | InsuranceIntroLinks | ContentTypeRichTextSecondaryContentLinks | TableFootnoteLinks | ContentSectionDescriptionLinks;
  isDark?: boolean;
  theme: Theme;
  isSmallScreen?: boolean;
}): Options => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (_: unknown, children) => <Typography component="h1" variant="header1" margin="0.625rem 0" {...docProps}>
				{parseChildren(children)}
			</Typography>,
    [BLOCKS.HEADING_2]: (_: unknown, children) => <Typography component="h2" variant="header2" margin="0.75rem 0" {...docProps}>
				{parseChildren(children)}
			</Typography>,
    [BLOCKS.HEADING_3]: (_: unknown, children) => <Typography component="h3" variant="header3" margin="0.875rem 0" {...docProps}>
				{parseChildren(children)}
			</Typography>,
    [BLOCKS.HEADING_4]: (_: unknown, children) => <Typography component="h4" variant="header4" margin="1rem 0" {...docProps}>
				{parseChildren(children)}
			</Typography>,
    [BLOCKS.EMBEDDED_ASSET]: (node: Block) => {
      const isFullWidth = docProps.fullWidth;
      const assetsData = links?.assets?.block;
      if (!assetsData) {
        logWarning("RichText EMBEDDED_ASSET: Couldn't find assets data in links.");
        return null;
      }
      const target = node.data.target as ContentfulRichTextNodeTarget;
      if (target.sys.linkType !== 'Asset') {
        logWarning('RichText EMBEDDED_ASSET: target link type was not an Asset.');
        return null;
      }
      const asset = assetsData.find(asset => asset?.sys.id === target.sys.id);
      if (!asset) {
        logWarning(`RichText EMBEDDED_ASSET: Couldn't find asset data for rich text content: ${target.sys.id}.`);
        return null;
      }
      if (asset.contentType === 'text/html') {
        return <iframe width={isFullWidth ? '700' : '350'} height={isFullWidth ? '394' : '200'} src={`https://www.youtube.com/embed/${asset?.fileName as string}`} title={asset?.description ?? ''} style={{
          border: 0
        }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
      }
      if (['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(asset.contentType || '') && asset.url) {
        return <ImageWrapper src={asset.url} alt={asset?.description ?? ''} style={{
          maxWidth: '100%'
        }} width="1200" height="800" />;
      }
      return null;
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Block) => {
      const target = node.data.target as ContentfulRichTextNodeTarget;
      const targetSysId = target.sys.id;
      const blockEntries = links?.entries.block;
      const matchingBlockEntry = blockEntries?.find((entry: Maybe<Entry>) => entry?.sys.id === targetSysId);
      if (!matchingBlockEntry) {
        return;
      }
      return <EntryBlockRender matchingBlockEntry={matchingBlockEntry} target={target} targetSysId={targetSysId} isSmallScreen={!!isSmallScreen} />;
    },
    [BLOCKS.PARAGRAPH]: (_: unknown, children) => <Typography component="p" variant="bodyMediumBook" color="text.secondary" {...docProps}>
				{parseChildren(children)}
			</Typography>,
    [BLOCKS.OL_LIST]: (_: unknown, children) => <Typography component="ol" variant="bodyMediumBook" color="text.secondary" {...{
      ...docProps,
      ...listSx,
      sx: {
        ...(docProps.sx as Record<string, unknown>),
        ...listSx,
        '& p': {
          textAlign: 'left'
        }
      }
    }}>
				{children as string}
			</Typography>,
    [BLOCKS.UL_LIST]: (_: unknown, children) => <Typography component="ul" variant="bodyMediumBook" color="text.secondary" {...{
      ...docProps,
      ...listSx,
      sx: {
        ...(docProps.sx as Record<string, unknown>),
        ...listSx,
        '& p': {
          textAlign: 'left'
        }
      }
    }}>
				{children as string}
			</Typography>,
    [INLINES.HYPERLINK]: (node: Inline, children) => {
      let target = '_self';
      const uri = normalizeDomainLink(node.data.uri as string);
      const {
        isExternalUrl
      } = uri ? getUrlType(uri) : {
        isExternalUrl: false
      };
      if (isExternalUrl && uri) {
        try {
          const url = new URL(uri);
          if (url.hostname !== globalThis?.location?.hostname) {
            target = '_blank';
          }
        } catch (error) {
          console.warn('Invalid URL provided:', error);
        }
      }
      const {
        config
      } = useAppContext();
      const {
        officeInfo
      } = useFacilityContext();
      return <Link href={uri} color={isDark ? theme.palette.text.light : theme.palette.text.interactive} underline="always" target={target} onClick={() => handleLinkClickDataLayer(config, uri, getInnerTextForAnalytics(children), officeInfo)} {...docProps}>
					{children as string}
				</Link>;
    },
    [INLINES.ENTRY_HYPERLINK]: (node: Inline, children) => {
      const target = node.data.target as ContentfulRichTextNodeTarget;
      const entryData = links?.entries?.hyperlink;
      if (!entryData && target.sys.linkType === 'Entry') {
        return <Link color={isDark ? theme.palette.text.light : theme.palette.text.interactive} underline="always" {...docProps}>
						{children as string}
					</Link>;
      }
      if (!entryData) {
        logWarning("RichText ENTRY_HYPERLINK: Couldn't find entries data in links.");
        return null;
      }
      if (target.sys.linkType !== 'Entry') {
        logWarning('RichText ENTRY_HYPERLINK: target link type was not an Entry.');
        return null;
      }
      const link = entryData.find(entry => entry?.sys.id === target.sys.id) as HyperLinkEntry;
      if (!link) {
        logWarning(`RichText ENTRY_HYPERLINK: Couldn't find entries data for rich text content: ${target.sys.id}.`);
        return null;
      }
      let path = '';
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        config
      } = useAppContext();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        officeInfo
      } = useFacilityContext();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        officeInfo: officeLocationInfo
      } = useOfficeLocationContext();
      path = resolveEntryLink({
        path: link?.path,
        typeName: link.__typename,
        config,
        officeInfo,
        officeLocationInfo
      });
      path = normalizeDomainLink(path);
      if (!path) {
        logWarning(`RichText ENTRY_HYPERLINK: Couldn't find Page Path data for ${link.__typename} type rich text content: ${target.sys.id}.`);
        return null;
      }
      let linkTarget = '_self';
      const {
        isExternalUrl
      } = getUrlType(path);
      if (isExternalUrl) {
        try {
          const url = new URL(path);
          if (url.hostname !== globalThis?.location?.hostname) {
            linkTarget = '_blank';
          }
        } catch (error) {
          console.warn('Invalid URL provided:', error);
        }
      }
      return <Link href={path} target={linkTarget} color={isDark ? theme.palette.text.light : theme.palette.text.interactive} underline="always" onClick={() => handleLinkClickDataLayer(config, path, getInnerTextForAnalytics(children), officeInfo)} {...docProps}>
					{children as string}
				</Link>;
    }
  } as RenderNode,
  renderText: text => {
    // @see https://www.smartercontent.info/post/how-to-add-line-breaks-to-any-text-field-in-contentful
    let lineBreakCount = 0;
    return text.split('\n').reduce((children, textSegment) => {
      const parsedText = parse(textSegment) as React.ReactNode;
      if (children.length === 0) {
        return [parsedText];
      }
      lineBreakCount += 1;
      return [...children, <br key={`linebreak-${textSegment}-${lineBreakCount}`} />, parsedText];
    }, [] as React.ReactNode[]);
  }
});